/**
 * Stable semantic identities for named neon themes.
 *
 * This module deliberately has no React/store dependencies. The persisted app
 * store can therefore validate and migrate theme identities without importing
 * `neonTheme.ts`, which reads the store for its hooks.
 */

export const NEON_CHROME_THEME_IDS = [
  'sunlight',
  'desert-day',
  'hi-vis',
  'blueprint',
  'sandstone',
  'slate',
  'ember',
  'ocean',
  'forest',
  'rose-gold',
  'lavender',
  'soft-sky',
  'graphite',
  'copper',
  'arctic',
  'plasma',
  'toxic',
  'royal',
  'crimson',
  'gold',
  'aurora',
  'orchid',
  'volt',
  'midnight',
  'black-gold',
  'gas-flare',
] as const;

export type NeonChromeThemeId = (typeof NEON_CHROME_THEME_IDS)[number];

/** App default chrome theme — oilfield Black Gold (dark + high contrast). */
export const DEFAULT_NEON_CHROME_THEME_ID: NeonChromeThemeId = 'black-gold';

const THEME_ID_SET = new Set<string>(NEON_CHROME_THEME_IDS);

export function normalizeNeonChromeThemeId(value: unknown): NeonChromeThemeId | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toLowerCase();
  return THEME_ID_SET.has(normalized) ? normalized as NeonChromeThemeId : null;
}

/**
 * Migrate an older accent-only preference without guessing how it was chosen.
 *
 * null/empty accents mean the historical Black Gold default. Every explicit
 * color remains custom because pre-v12 storage could not distinguish a theme
 * chip from the free color wheel, even when the hex happens to be unique.
 */
export function inferLegacyNeonChromeThemeId(
  accent: unknown,
): NeonChromeThemeId | null {
  if (accent == null || accent === '') return DEFAULT_NEON_CHROME_THEME_ID;
  return null;
}
